import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useResponsive } from '../../components/vm/useResponsive';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";

// ============================================================================
// CUSTOM SVG ICONS
// ============================================================================
const FaTimes = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
  </svg>
);

const FaExclamationTriangle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const FaInfoCircle = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

const FaCheck = ({ size = 16, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={style}>
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

// ============================================================================
// SENTRY LOGGING HELPERS
// ============================================================================
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'create-image-modal' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'create-image-modal' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'create-image-modal',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// ============================================================================
// ERROR MESSAGES MAPPING
// ============================================================================
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SESSION_EXPIRED': 'Session expired. Please log in again.',
  'IMAGE_CREATE_FAILED': 'Unable to create image. Please try again.',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'SNAPSHOT_NOT_FOUND': 'Selected snapshot not found. Please try again.',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable',
  'INVALID_USER_PROFILE': 'Invalid user profile format',
  'INCOMPLETE_PROFILE': 'Please complete your profile to continue',
  'UNEXPECTED_PAYLOAD': 'Invalid request format',
  'INVALID_JSON': 'Invalid request format',
  'API_ERROR': 'Service temporarily unavailable. Please try again later',
  'IMAGE_LIST_FAILED': 'Unable to load images. Please refresh the page',
  'IMAGE_DELETE_FAILED': 'Unable to delete image. Please try again',
  'IMAGE_DETAILS_FAILED': 'Unable to load image details. Please try again',
  'NO_SNAPSHOTS_AVAILABLE': 'No snapshots available for image creation',
  'INVALID_PARAMETERS': 'Invalid parameters provided. Please check your input and try again',
  'IMAGE_NOT_FOUND': 'Image not found or you do not have permission to access it',
  'INVALID_SNAPSHOT_ID_FORMAT': 'Invalid snapshot format. Please select a valid snapshot',
  'UNSUPPORTED_REGION': 'This snapshot is in an unsupported region',
  'DATABASE_ERROR': 'Service temporarily unavailable',
  'SERVER_ERROR': 'Service temporarily unavailable',
  'INVALID_DATA': 'Invalid input data',
  'INVALID_REQUEST': 'Invalid request format',
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  'DB_ERROR': 'Service temporarily unavailable',
  'INVALID_ID_FORMAT': 'Invalid snapshot selection. Please choose a different snapshot',
  'INVALID_IMAGE_PARAMETERS': 'Please check your image name and try again',
  'IMAGE_ALREADY_EXISTS_FOR_SNAPSHOT': 'An image already exists for this snapshot. Please delete the existing image first.',
  'NETWORK_ERROR': 'Network connection failed. Please check your internet connection and try again.',
  // Network Errors
'OFFLINE': "You're offline. Check your connection.",
'TIMEOUT': "Request timed out. Please try again.",
'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
'NO_RESPONSE': "No response from server. Please try again.",
'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again.",
'TOO_MANY_REQUESTS': "Too many requests. Please wait before trying again."
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const validateImageName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Image name is required' };
  }
  
  const trimmedName = name.trim();
  
  if (trimmedName.length === 0) {
    return { valid: false, error: 'Image name cannot be empty' };
  }
  
  if (trimmedName.length < 3) {
    return { valid: false, error: 'Image name must be at least 3 characters' };
  }
  
  if (trimmedName.length > 30) {
    return { valid: false, error: 'Image name must be 30 characters or less' };
  }
  
  if (!/^[a-zA-Z0-9\s\-_\.]+$/.test(trimmedName)) {
    return { valid: false, error: 'Use only letters, numbers, spaces, hyphens, underscores, and dots' };
  }
  
  if (trimmedName.startsWith('-') || trimmedName.startsWith('_')) {
    return { valid: false, error: 'Name cannot start with hyphen or underscore' };
  }
  
  if (trimmedName.endsWith('-') || trimmedName.endsWith('_')) {
    return { valid: false, error: 'Name cannot end with hyphen or underscore' };
  }
  
  return { valid: true };
};
const validateSnapshot = (snapshotId, snapshots) => {
  if (snapshotId === null || snapshotId === undefined || snapshotId === '') {
    return { valid: false, error: 'Please select a snapshot' };
  }
  
  const foundSnapshot = snapshots.find(s => {
    return s.snapshot_id === snapshotId || String(s.snapshot_id) === String(snapshotId);
  });
  
  if (!foundSnapshot) {
    logError('Snapshot validation failed - not found', {
      snapshotId: snapshotId,
      availableSnapshots: snapshots.map(s => s.snapshot_id),
      availableSnapshotsCount: snapshots.length
    });
    return { valid: false, error: 'Selected snapshot not found' };
  }
  
  return { valid: true };
};
const getSafeErrorMessage = (err) => {
  if (!err || typeof err !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Check error code against mapping
  if (err.code && ERROR_MESSAGES[err.code]) {
    return ERROR_MESSAGES[err.code];
  }
  
  // Use interceptor's user-friendly message
  if (err.error) {
    return err.error;
  }
  
  return 'Something went wrong. Please try again.';
};

const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return { message: "You're offline. Check your connection.", code: 'OFFLINE' };
  }
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return { message: "Request timed out. Please try again.", code: 'TIMEOUT' };
  }
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return { message: "Unable to reach server. Please try later.", code: 'SERVER_UNREACHABLE' };
  }
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return { message: "You're offline. Check your connection.", code: 'OFFLINE' };
  }
  if (error.request && !error.response) {
    return { message: "No response from server. Please try again.", code: 'NO_RESPONSE' };
  }
  return { message: "Something went wrong. Please try again.", code: 'UNKNOWN_NETWORK_ERROR' };
};
// ============================================================================
// IMAGE ICON COMPONENT
// ============================================================================
const ImageIcon = ({ size = 'medium' }) => {
  let width, height;
  
  if (size === 'large') {
    width = '48px';
    height = '48px';
  } else if (size === 'medium') {
    width = '40px';
    height = '40px';
  } else if (size === 'small') {
    width = '24px';
    height = '24px';
  } else {
    width = '40px';
    height = '40px';
  }
  
  return (
    <div style={{ width, height, position: 'relative', flexShrink: 0 }}>
      <svg viewBox="0 0 24 24" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
        <rect
          x="2" y="4" width="20" height="16" rx="2"
          fill="#1b2b1a"
          stroke="#e0eacc"
          strokeWidth="1"
        />
        <path
          d="M4 16 L10 12 L14 14 L20 10 L20 16 L4 16 Z"
          fill="#e0eacc"
          fillOpacity="0.2"
        />
        <circle
          cx="8" cy="8" r="2"
          fill="#e0eacc"
        />
      </svg>
    </div>
  );
};

// ============================================================================
// INLINE STYLES
// ============================================================================
const getStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '260px',
    padding: '24px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  iconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '12px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdown: {
    position: 'relative',
  },
  dropdownTrigger: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    zIndex: 100,
    maxHeight: '200px',
    overflow: 'auto',
  },
  dropdownItem: {
    padding: '10px 14px',
    color: 'white',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  infoBox: {
    backgroundColor: 'rgba(59, 130, 246, 0.08)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    padding: '12px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  infoText: {
    color: '#93c5fd',
    fontSize: '12px',
    margin: 0,
    lineHeight: '1.5',
  },
  warningBox: {
    backgroundColor: '#1a0b0b',
    border: '1px solid #3a1818',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
});

// ============================================================================
// EXISTING IMAGE WARNING COMPONENT
// ============================================================================
const ExistingImageWarning = ({ imageInfo, loading }) => {
  if (loading) {
    return (
      <div style={{
        marginTop: '8px',
        padding: '10px 12px',
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[0, 1, 2].map((i) => (
              <div 
                key={i}
                style={{
                  width: '4px',
                  height: '4px',
                  backgroundColor: '#e0eacc',
                  borderRadius: '50%',
                  animation: `bounce 1s ease ${i * 0.15}s infinite`
                }}
              />
            ))}
          </div>
          <span style={{ color: '#9ca3af', fontSize: '12px' }}>Checking for existing images...</span>
        </div>
      </div>
    );
  }
  
  if (imageInfo?.hasImage) {
    return (
      <div style={{
        marginTop: '8px',
        padding: '12px',
        backgroundColor: '#1a0b0b',
        border: '1px solid #3a1818',
        borderRadius: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <FaExclamationTriangle size={14} style={{ color: '#ff8080', marginTop: '2px', flexShrink: 0 }} />
          <div>
            <div style={{ color: '#ff8080', fontWeight: '500', fontSize: '12px', marginBottom: '4px' }}>
              Existing Image Found
            </div>
            <div style={{ color: '#d1d5db', fontSize: '12px', lineHeight: '1.4' }}>
              An image named "<strong>{imageInfo.image?.name}</strong>" already exists for this snapshot.
              You must delete it before creating a new image.
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return null;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CreateImageModal = ({ isOpen, onClose, onSubmit, snapshots, existingImageNames = [] }) => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();
  
  const [selectedSnapshot, setSelectedSnapshot] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');
  const [nameCharCount, setNameCharCount] = useState(0);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  
  const [snapshotImageMap, setSnapshotImageMap] = useState(new Map());
  const [checkingExistingImage, setCheckingExistingImage] = useState(false);
  
  const [nameError, setNameError] = useState('');
  const [snapshotError, setSnapshotError] = useState('');
  
  const maxRetries = 3;
  const retryAttempts = useRef(0);
  const snapshotDropdownRef = useRef(null);
  const modalRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const snapshotImageCacheRef = useRef(new Map());

  
  const isSubmitting = loading;
  const styles = getStyles(isSubmitting);
  
  const selectedSnapshotData = snapshots?.find(s => 
    s.snapshot_id === selectedSnapshot || String(s.snapshot_id) === String(selectedSnapshot)
  );
  
  const currentImageInfo = snapshotImageMap.get(selectedSnapshot);

  // Convert errors to toast notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      notification.error(error);
      setLastShownError(error);
      setError(null);
    }
  }, [error, notification, lastShownError]);

  // Set Sentry context when modal opens
  useEffect(() => {
    if (isOpen) {
      Sentry.setContext("create-image-modal", {
        isOpen,
        snapshotsCount: snapshots?.length || 0,
        selectedSnapshot,
        hasName: !!name,
        nameLength: name.length,
        isMobile,
        isTablet
      });
      
      logInfo('Create image modal opened', {
        snapshotsCount: snapshots?.length || 0,
        availableSnapshots: snapshots?.map(s => ({ id: s.id, name: s.name })) || []
      });
      
      logUserAction('create_image_modal_opened', {
        snapshotsCount: snapshots?.length || 0
      });
    }
  }, [isOpen, snapshots, selectedSnapshot, name, isMobile, isTablet]);


const checkSnapshotHasImage = useCallback(async (snapshotId) => {
  if (!snapshotId) return { hasImage: false, image: null };
  
  if (snapshotImageCacheRef.current.has(snapshotId)) {
    return snapshotImageCacheRef.current.get(snapshotId);
  }
  
  try {
    setCheckingExistingImage(true);
    const response = await api.getSnapshotImageRelationship(snapshotId);
    
    if (response.success) {
      const imageInfo = {
        hasImage: response.has_image,
        image: response.image
      };
      
      snapshotImageCacheRef.current.set(snapshotId, imageInfo);
      setSnapshotImageMap(new Map(snapshotImageCacheRef.current));
      
      logInfo('Snapshot image relationship checked', {
        snapshotId,
        hasImage: response.has_image,
        imageName: response.image?.name || null
      });
      
      return imageInfo;
    }
  } catch (err) {
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return { hasImage: false, image: null };
    }
    
    // Position 2: Network errors (no status property from interceptor)
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      logError('Network error in checkSnapshotHasImage', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        snapshotId
      }, err);
      return { hasImage: false, image: null };
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      logError('Rate limit in checkSnapshotHasImage', { snapshotId }, err);
      return { hasImage: false, image: null };
    }
    
    // Position 4: All other errors - log and return default
    logError('Error checking snapshot image relationship', {
      snapshotId,
      code: err.code,
      status: err.status,
      message: err.error
    }, err);
  } finally {
    setCheckingExistingImage(false);
  }
  
  return { hasImage: false, image: null };
}, [handleAuthError]);

// Reset form
  const resetForm = useCallback(() => {
  const initialSnapshot = snapshots?.length > 0 ? snapshots[0].snapshot_id : '';
  
  setSelectedSnapshot(initialSnapshot);
  setName('');
  setNameCharCount(0);
  setError(null);
  setLastShownError('');
  setNameError('');
  setSnapshotError('');
  setIsSnapshotDropdownOpen(false);
  snapshotImageCacheRef.current = new Map();
  setSnapshotImageMap(new Map());
  setLoading(false);
  isSubmittingRef.current = false;
  
  logInfo('Form reset completed', {
    initialSnapshot,
    snapshotsCount: snapshots?.length || 0
  });
}, [snapshots]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      
      // Check image status for initial snapshot
      if (snapshots?.length > 0) {
        checkSnapshotHasImage(snapshots[0].snapshot_id);
      }
      
      logInfo('Form reset on modal open');
    }
  }, [isOpen, resetForm, snapshots, checkSnapshotHasImage]);

  // Handle click outside for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (snapshotDropdownRef.current && !snapshotDropdownRef.current.contains(event.target)) {
        if (isSnapshotDropdownOpen) {
          logUserAction('dropdown_closed_outside_click');
          setIsSnapshotDropdownOpen(false);
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isSnapshotDropdownOpen]);

  // Handle name change
  const handleNameChange = useCallback((e) => {
    const value = e.target.value;
    setName(value);
    setNameCharCount(value.length);
    
    const validation = validateImageName(value.trim());
    if (!validation.valid) {
      setNameError(validation.error);
    } else if (existingImageNames.includes(value.trim())) {
      setNameError('An image with this name already exists');
      
      logInfo('Image name validation failed - duplicate name', {
        attemptedName: value.trim(),
        existingNames: existingImageNames.length
      });
    } else {
      setNameError('');
    }
    
    logUserAction('image_name_changed', {
      nameLength: value.length,
      isValid: validation.valid
    });
  }, [existingImageNames]);

  // Toggle dropdown
  const toggleSnapshotDropdown = useCallback((e) => {
    e.stopPropagation();
    const newState = !isSnapshotDropdownOpen;
    setIsSnapshotDropdownOpen(newState);
    
    logUserAction('snapshot_dropdown_toggled', {
      opened: newState,
      currentSelection: selectedSnapshot,
      snapshotsCount: snapshots?.length || 0
    });
  }, [isSnapshotDropdownOpen, selectedSnapshot, snapshots]);

  // Handle snapshot selection
  const handleSnapshotSelect = useCallback(async (snapshotId) => {
    const previousSelection = selectedSnapshot;
    const selectedSnapshotObj = snapshots?.find(s => s.snapshot_id === snapshotId);
    
    setSelectedSnapshot(snapshotId);
    setIsSnapshotDropdownOpen(false);
    
    const validation = validateSnapshot(snapshotId, snapshots || []);
    if (!validation.valid) {
      setSnapshotError(validation.error);
    } else {
      setSnapshotError('');
    }
    
    // Check if this snapshot already has an image
    if (validation.valid) {
      await checkSnapshotHasImage(snapshotId);
    }
    
    logUserAction('snapshot_selected', {
      previousSelection,
      newSelection: snapshotId,
      snapshotName: selectedSnapshotObj?.name,
      isValid: validation.valid
    });
    
    logInfo('Snapshot selection changed', {
      from: previousSelection,
      to: snapshotId,
      snapshotName: selectedSnapshotObj?.name
    });
  }, [selectedSnapshot, snapshots, checkSnapshotHasImage]);

  const handleSubmit = useCallback(async () => {
  if (isSubmittingRef.current) {
    return;
  }
  
  isSubmittingRef.current = true;
  
  const startTime = performance.now();
  
  logUserAction('image_creation_initiated', {
    imageName: name.trim(),
    selectedSnapshot,
    nameLength: name.length
  });
  
  // Validate name
  const nameValidation = validateImageName(name.trim());
  if (!nameValidation.valid) {
    setNameError(nameValidation.error);
    setError(nameValidation.error);
    
    logError('Image creation failed - name validation', {
      imageName: name.trim(),
      validationError: nameValidation.error
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  // Check duplicate name
  if (existingImageNames.includes(sanitizeText(name.trim()))) {
    setNameError('An image with this name already exists');
    setError('An image with this name already exists');
    
    logError('Image creation failed - duplicate name', {
      imageName: name.trim()
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  // Validate snapshot
  const snapshotValidation = validateSnapshot(selectedSnapshot, snapshots || []);
  if (!snapshotValidation.valid) {
    setSnapshotError(snapshotValidation.error);
    setError(snapshotValidation.error);
    
    logError('Image creation failed - snapshot validation', {
      selectedSnapshot,
      validationError: snapshotValidation.error
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  // Final check for existing image
  const imageInfo = await checkSnapshotHasImage(selectedSnapshot);
  if (imageInfo.hasImage) {
    const errorMsg = `Cannot create image: This snapshot already has an image named "${imageInfo.image?.name}". Please delete the existing image first.`;
    setError(errorMsg);
    
    logError('Image creation blocked - existing image found', {
      snapshotId: selectedSnapshot,
      existingImageId: imageInfo.image?.id,
      existingImageName: imageInfo.image?.name
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  // Check retry limits
  if (retryAttempts.current >= maxRetries) {
    const errorMsg = 'Maximum creation attempts reached. Please wait before trying again.';
    setError(errorMsg);
    
    logError('Image creation failed - max retries exceeded', {
      attempts: retryAttempts.current,
      maxRetries
    });
    
    isSubmittingRef.current = false;
    return;
  }
  
  retryAttempts.current++;
  setLoading(true);
  
  try {
    const imageData = {
      name: sanitizeText(name.trim())
    };
    
    logInfo('Submitting image creation request', {
      imageData,
      snapshotId: selectedSnapshot,
      attempt: retryAttempts.current
    });
    
    await onSubmit(selectedSnapshot, imageData);
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    logPerformance('image_creation_submit', duration, {
      imageName: imageData.name,
      snapshotId: selectedSnapshot,
      attempt: retryAttempts.current,
      success: true
    });
    
    logInfo('Image creation submitted successfully', {
      imageName: imageData.name,
      snapshotId: selectedSnapshot,
      duration: `${duration.toFixed(2)}ms`
    });
    
    logUserAction('image_creation_success', {
      imageName: imageData.name,
      snapshotId: selectedSnapshot
    });
    
    resetForm();
    onClose();
    retryAttempts.current = 0;
    
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'SESSION_EXPIRED' || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Network errors (interceptor sets code, no status)
    const networkErrorCodes = ['NETWORK_ERROR', 'OFFLINE', 'TIMEOUT', 'SERVER_UNREACHABLE', 'NO_RESPONSE'];
    if (networkErrorCodes.includes(err.code)) {
      setError(err.error); // Use interceptor's user-friendly message
      logError('Network error in handleSubmit', { 
        code: err.code, 
        message: err.error,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        imageName: name.trim(),
        snapshotId: selectedSnapshot
      }, err);
      return;
    }
    
    // Position 3: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      setError(err.error || 'Too many requests. Please wait before trying again.');
      logError('Rate limit in handleSubmit', {
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 4: Known error codes from ERROR_MESSAGES mapping
    if (err.code && ERROR_MESSAGES[err.code]) {
      setError(ERROR_MESSAGES[err.code]);
      logError('Create image error with known code', {
        code: err.code,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 5: Server errors
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      setError(err.error || 'Service temporarily unavailable. Please try again later.');
      logError('Server error in handleSubmit', {
        code: err.code,
        status: err.status,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      return;
    }
    
    // Position 6: Fallback - use interceptor's message or generic
    setError(err.error || 'Something went wrong. Please try again.');
    
    logError('Image creation failed', {
      imageName: name.trim(),
      snapshotId: selectedSnapshot,
      attempt: retryAttempts.current,
      duration: `${duration.toFixed(2)}ms`,
      code: err.code,
      status: err.status,
      message: err.error
    }, err);
    
  } finally {
    setLoading(false);
    isSubmittingRef.current = false;
  }
}, [
  name,
  selectedSnapshot,
  snapshots,
  existingImageNames,
  onSubmit,
  onClose,
  resetForm,
  checkSnapshotHasImage,
  handleAuthError
]);
  const handleClose = useCallback(() => {
    logUserAction('modal_close_initiated', {
      hasUnsavedChanges: !!(name.trim() || selectedSnapshot),
      imageName: name.trim(),
      selectedSnapshot
    });
    
    logInfo('Modal closing', {
      imageName: name.trim(),
      selectedSnapshot
    });
    
    setLoading(false);
    isSubmittingRef.current = false;
    onClose();
  }, [name, selectedSnapshot, onClose]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'N/A';
    }
  };

  // Check if form can be submitted
  const canSubmit = name.trim() && 
                    !nameError && 
                    !snapshotError && 
                    selectedSnapshot && 
                    !currentImageInfo?.hasImage &&
                    !checkingExistingImage;

  if (!isOpen) return null;

  // MOBILE LAYOUT
  if (isMobile || isTablet) {
    return (
      <>
        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes bounce {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(-4px); }
            }
            .custom-scrollbar {
              scrollbar-width: thin;
              scrollbar-color: #555555 #2a2a2a;
            }
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #2a2a2a;
              border-radius: 3px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #555555;
              border-radius: 3px;
            }
          `}
        </style>
        
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}>
          <div 
            ref={modalRef}
            style={{
              backgroundColor: '#121212',
              borderRadius: '12px',
              border: '1px solid #333',
              overflow: 'hidden',
              width: '100%',
              maxWidth: '400px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              borderBottom: '1px solid #222',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  padding: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#1a1a1a',
                  marginRight: '8px'
                }}>
                  <ImageIcon size="small" />
                </div>
                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', margin: 0 }}>
                  Create Custom Image
                </h2>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                style={{
                  padding: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                <FaTimes size={14} />
              </button>
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }} className="custom-scrollbar">
              {/* Snapshot Selection */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ color: '#d1d5db', fontWeight: '500', fontSize: '14px' }}>
                    Select Snapshot <span style={{ color: '#ff8080' }}>*</span>
                  </label>
                </div>
                <div style={{ position: 'relative' }} ref={snapshotDropdownRef}>
                  <div
                    onClick={!loading ? toggleSnapshotDropdown : undefined}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#1d1d1d',
                      border: `1px solid ${isSnapshotDropdownOpen ? '#e0eacc' : snapshotError ? '#ff8080' : '#333'}`,
                      borderRadius: '6px',
                      color: loading ? '#666' : '#fff',
                      fontSize: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxSizing: 'border-box'
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedSnapshotData?.name || 'Select Snapshot'}
                    </span>
                    <div style={{ display: 'flex', gap: '2px', marginLeft: '8px' }}>
                      {[0, 1, 2].map(i => (
                        <div key={i} style={{
                          width: '4px',
                          height: '4px',
                          backgroundColor: '#cad9e3',
                          borderRadius: '50%'
                        }} />
                      ))}
                    </div>
                  </div>
                  
                  {isSnapshotDropdownOpen && !loading && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: '#1d1d1d',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      maxHeight: '160px',
                      overflow: 'auto',
                      zIndex: 100
                    }} className="custom-scrollbar">
                      {snapshots && snapshots.length > 0 ? (
                        snapshots.map((snapshot) => (
                          <div
                            key={snapshot.snapshot_id}
                            onClick={() => handleSnapshotSelect(snapshot.snapshot_id)}
                            style={{
                              padding: '10px 12px',
                              color: String(selectedSnapshot) === String(snapshot.snapshot_id) ? '#e0eacc' : '#d1d5db',
                              backgroundColor: String(selectedSnapshot) === String(snapshot.snapshot_id) ? '#252525' : 'transparent',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            <div>{sanitizeText(snapshot.name)}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              {formatDate(snapshot.created_at)}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '14px' }}>
                          No snapshots available
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <ExistingImageWarning imageInfo={currentImageInfo} loading={checkingExistingImage} />
                
                {snapshotError && !currentImageInfo?.hasImage && (
                  <p style={{ marginTop: '4px', color: '#ff8080', fontSize: '12px' }}>{snapshotError}</p>
                )}
                {!snapshotError && !currentImageInfo?.hasImage && (
                  <p style={{ marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                    Select the snapshot to create an image from
                  </p>
                )}
              </div>
              
              {/* Image Name */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ color: '#d1d5db', fontWeight: '500', fontSize: '14px' }}>
                    Image Name <span style={{ color: '#ff8080' }}>*</span>
                  </label>
                  <span style={{ fontSize: '12px', color: nameCharCount > 25 ? '#ff8080' : '#6b7280' }}>
                    {nameCharCount}/30
                  </span>
                </div>
                <input
                  type="text"
                  value={name}
                  onChange={handleNameChange}
                  placeholder="e.g., barrack-server-image"
                  maxLength={30}
disabled={isSubmitting || currentImageInfo?.hasImage}                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#1d1d1d',
                    border: `1px solid ${nameError ? '#ff8080' : '#333'}`,
                    borderRadius: '6px',
                    color: loading ? '#666' : '#fff',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                {nameError ? (
                  <p style={{ marginTop: '4px', color: '#ff8080', fontSize: '12px' }}>{nameError}</p>
                ) : (
                  <p style={{ marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                    Give your image a descriptive name
                  </p>
                )}
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    fontWeight: '500',
                    fontSize: '14px',
                    border: 'none',
                    cursor: canSubmit && !loading ? 'pointer' : 'not-allowed',
                    backgroundColor: canSubmit && !loading ? '#e0eacc' : '#4a4a4a',
                    color: canSubmit && !loading ? '#000' : '#9ca3af',
                    minHeight: '44px'
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(0,0,0,0.2)',
                        borderTopColor: '#000',
                        borderRadius: '50%',
                        animation: 'spin 0.7s linear infinite',
                        marginRight: '8px'
                      }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaCheck size={12} style={{ marginRight: '8px' }} />
                      Create Image
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleClose}
                  disabled={loading}
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: '#cad9e3',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    border: '1px solid #cad9e3',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // DESKTOP LAYOUT
  return (
    <>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-4px); }
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #555555 #2a2a2a;
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #2a2a2a;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #555555;
            border-radius: 3px;
          }
        `}
      </style>
      
      <div style={styles.overlay}>
        <div style={styles.modal}>
          {/* LEFT PANEL - Form */}
          <div style={styles.leftPanel}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.iconWrapper}>
                <ImageIcon size="small" />
              </div>
              <div>
                <h2 style={styles.title}>Create Custom Image</h2>
                <p style={styles.subtitle}>Create reusable image from snapshot</p>
              </div>
            </div>

            {/* Snapshot Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>
                Select Snapshot<span style={styles.required}>*</span>
              </label>
              <div style={styles.dropdown} ref={snapshotDropdownRef}>
                <div
                  onClick={!isSubmitting ? toggleSnapshotDropdown : undefined}
                  style={{
                    ...styles.dropdownTrigger,
                    borderColor: isSnapshotDropdownOpen ? '#e0eacc' : snapshotError ? '#ff6b6b' : '#333'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedSnapshotData ? sanitizeText(selectedSnapshotData.name) : 'Select Snapshot'}
                    </div>
                    {selectedSnapshotData && (
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {formatDate(selectedSnapshotData.created_at)}
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#6b7280', fontSize: '10px' }}>▼</span>
                </div>
                
                  {isSnapshotDropdownOpen && !isSubmitting && (
                  <div style={styles.dropdownMenu} className="custom-scrollbar">
                    {snapshots && snapshots.length > 0 ? (
                      snapshots.map((snapshot) => (
                        <div
                          key={snapshot.snapshot_id}
                          onClick={() => handleSnapshotSelect(snapshot.snapshot_id)}
                          style={{
                            ...styles.dropdownItem,
                            backgroundColor: String(selectedSnapshot) === String(snapshot.snapshot_id) ? '#252525' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#252525'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = String(selectedSnapshot) === String(snapshot.snapshot_id) ? '#252525' : 'transparent'}
                        >
                          <div style={{ marginBottom: '2px' }}>{sanitizeText(snapshot.name)}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {formatDate(snapshot.created_at)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ ...styles.dropdownItem, cursor: 'default', color: '#6b7280' }}>
                        No snapshots available
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <ExistingImageWarning imageInfo={currentImageInfo} loading={checkingExistingImage} />
              
              {snapshotError && !currentImageInfo?.hasImage ? (
                <div style={styles.errorText}>{snapshotError}</div>
              ) : !currentImageInfo?.hasImage && (
                <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>
                  Select the snapshot to create an image from
                </p>
              )}
            </div>

            {/* Image Name */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label style={styles.label}>
                  Image Name<span style={styles.required}>*</span>
                </label>
                <span style={{ fontSize: '11px', color: nameCharCount > 25 ? '#ff6b6b' : '#6b7280' }}>
                  {nameCharCount}/30
                </span>
              </div>
         <input
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="e.g., barrack-server-image"
                disabled={isSubmitting || currentImageInfo?.hasImage}
                maxLength={30}
                style={{
                  ...styles.input,
                  borderColor: nameError ? '#ff6b6b' : '#333'
                }}
                onFocus={(e) => !isSubmitting && (e.target.style.borderColor = '#e0eacc')}
                onBlur={(e) => e.target.style.borderColor = nameError ? '#ff6b6b' : '#333'}
              />
              {nameError ? (
                <div style={styles.errorText}>{nameError}</div>
              ) : (
                <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>
                  Give your image a descriptive name
                </p>
              )}
            </div>

            {/* Info Box */}
            <div style={styles.infoBox}>
              <FaInfoCircle size={14} style={{ color: '#60a5fa', marginTop: '2px', flexShrink: 0 }} />
              <p style={styles.infoText}>
                Images can be used to quickly deploy new VMs with pre-configured settings and software.
              </p>
            </div>
          </div>

          {/* RIGHT PANEL - Summary */}
          <div style={styles.rightPanel}>
            <h3 style={{
              color: '#6b7280',
              fontSize: '11px',
              letterSpacing: '1.5px',
              marginTop: 0,
              marginBottom: '20px'
            }}>SUMMARY</h3>
            
            {selectedSnapshotData && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <p style={styles.summaryLabel}>Source Snapshot</p>
                  <p style={styles.summaryValue}>{sanitizeText(selectedSnapshotData.name)}</p>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <p style={styles.summaryLabel}>Created</p>
                  <p style={{ ...styles.summaryValue, fontSize: '13.5px' }}>
                    {formatDate(selectedSnapshotData.created_at)}
                  </p>
                </div>

                {currentImageInfo?.hasImage && (
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: 'rgba(255,128,128,0.1)',
                    border: '1px solid rgba(255,128,128,0.3)',
                    borderRadius: '6px',
                    marginBottom: '16px'
                  }}>
                    <p style={{ color: '#ff8080', fontSize: '11px', margin: 0 }}>
                      ⚠️ Image already exists
                    </p>
                  </div>
                )}
              </>
            )}

            {name && (
              <div style={{ marginBottom: '16px' }}>
                <p style={styles.summaryLabel}>Image Name</p>
                <p style={styles.summaryValue}>{sanitizeText(name)}</p>
              </div>
            )}

            <div style={{ borderTop: '1px solid #222', paddingTop: '20px', marginTop: 'auto' }}></div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              style={{
                ...styles.submitBtn,
                opacity: (!canSubmit && !isSubmitting) ? 0.5 : 1,
                backgroundColor: canSubmit && !isSubmitting ? '#e0eacc' : '#3d4435',
                color: canSubmit && !isSubmitting ? 'black' : '#a0a89a',
                boxShadow: canSubmit && !isSubmitting ? '0 4px 14px rgba(224,234,204,0.25)' : 'none'
              }}
            >
              {isSubmitting ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={styles.spinner}></div>
                  <span>Creating...</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaCheck size={14} />
                  <span>Create Image</span>
                </div>
              )}
            </button>
            
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateImageModal;